import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { formatCurrency, formatDateTime, getCheckInStatusClass, getPaymentStatusClass, CHECK_IN_STATUS_LABELS, PAYMENT_STATUS_LABELS } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Edit, Save, X, User, Phone, Calendar, Users, Hash, Clock } from 'lucide-react';
import { LoadingBlock } from '../../components/common/States';

export default function CheckInDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    adminApi.getCheckIn(id)
      .then(res => {
        setData(res.data);
        setEditForm({
          customer_name: res.data.checkin.customer_name,
          phone: res.data.checkin.phone,
          guests: res.data.checkin.guests,
          arrival_time: res.data.checkin.arrival_time || '',
          notes: res.data.checkin.notes || '',
          check_in_status: res.data.checkin.check_in_status,
        });
      })
      .catch(() => toast.error('Failed to load check-in'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.updateCheckIn(id, editForm);
      setData(d => ({ ...d, checkin: updated.data }));
      setEditing(false);
      toast.success('Check-in updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setSaving(true);
    try {
      const updated = await adminApi.updateCheckIn(id, { check_in_status: newStatus });
      setData(d => ({ ...d, checkin: updated.data }));
      setEditForm(f => ({ ...f, check_in_status: newStatus }));
      toast.success(`Status updated to ${CHECK_IN_STATUS_LABELS[newStatus]}`);
    } catch { toast.error('Failed to update status'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <AdminShell title="Check-in Details">
        <LoadingBlock rows={6} />
      </AdminShell>
    );
  }

  if (!data) {
    return (
      <AdminShell title="Check-in Details">
        <p className="text-[#B9B9B9]">Check-in not found.</p>
      </AdminShell>
    );
  }

  const { checkin, payment, audit_logs } = data;

  return (
    <AdminShell
      title="Check-in Details"
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/checkins')} className="gap-1.5 text-[#B9B9B9] text-xs">
            <ArrowLeft size={13} /> Back
          </Button>
          {!editing ? (
            <Button size="sm" className="btn-gold gap-1.5 text-xs h-8" onClick={() => setEditing(true)}>
              <Edit size={13} /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" className="text-xs h-8 text-[#B9B9B9]" onClick={() => setEditing(false)}>
                <X size={13} /> Cancel
              </Button>
              <Button size="sm" className="btn-gold gap-1.5 text-xs h-8" onClick={handleSave} disabled={saving}>
                <Save size={13} /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left — Guest Details */}
        <div className="space-y-4">
          <div className="surface-card card-highlight rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2]">Guest Information</h2>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2.5 py-1 rounded-full ${getCheckInStatusClass(checkin.check_in_status)}`}>
                  {CHECK_IN_STATUS_LABELS[checkin.check_in_status]}
                </span>
              </div>
            </div>

            {editing ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label className="text-[#B9B9B9] text-xs">Name</Label>
                  <Input value={editForm.customer_name} onChange={e => setEditForm(f => ({...f, customer_name: e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[#B9B9B9] text-xs">Phone</Label>
                  <Input value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[#B9B9B9] text-xs">Guests</Label>
                  <Input type="number" min={1} max={20} value={editForm.guests} onChange={e => setEditForm(f => ({...f, guests: parseInt(e.target.value)}))} className="bg-[#111111] border-[#2A2A2A]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[#B9B9B9] text-xs">Arrival Time</Label>
                  <Input type="time" value={editForm.arrival_time} onChange={e => setEditForm(f => ({...f, arrival_time: e.target.value}))} className="bg-[#111111] border-[#2A2A2A]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[#B9B9B9] text-xs">Notes</Label>
                  <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} className="bg-[#111111] border-[#2A2A2A] resize-none" rows={2} />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {[
                  { icon: Hash, label: 'Reference', value: checkin.reference_id, mono: true },
                  { icon: User, label: 'Name', value: checkin.customer_name },
                  { icon: Phone, label: 'Phone', value: checkin.phone },
                  { icon: Users, label: 'Guests', value: checkin.guests },
                  { icon: Calendar, label: 'Visit Date', value: checkin.visit_date },
                  { icon: Clock, label: 'Arrival Time', value: checkin.arrival_time || '—' },
                ].map(row => (
                  <div key={row.label} className="flex items-start justify-between gap-3">
                    <span className="text-[#6F6F6F] text-xs flex items-center gap-1.5"><row.icon size={11} />{row.label}</span>
                    <span className={`text-sm text-right ${row.mono ? 'text-mono text-[#D4A843]' : 'text-[#F2F2F2]'}`}>{row.value}</span>
                  </div>
                ))}
                {checkin.notes && (
                  <div className="border-t border-[#2A2A2A] pt-3">
                    <p className="text-[#6F6F6F] text-xs mb-1">Notes</p>
                    <p className="text-[#B9B9B9] text-sm">{checkin.notes}</p>
                  </div>
                )}
                {checkin.source && (
                  <div className="flex items-start justify-between gap-3">
                    <span className="text-[#6F6F6F] text-xs">Source</span>
                    <span className="text-sm text-[#B9B9B9]">{checkin.source}</span>
                  </div>
                )}
                {(checkin.promoter || checkin.p_code_entered) && (
                  <div className="border-t border-[#2A2A2A] pt-3 mt-1 space-y-2">
                    <p className="text-[#6F6F6F] text-xs uppercase tracking-wider">Promoter Attribution</p>
                    {checkin.promoter ? (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-[#6F6F6F] text-xs">Credited to</span>
                        <span className="text-right">
                          <div className="font-mono text-[#D4A843] text-sm tabular-nums">
                            {checkin.promoter.short_code || '—'} · {checkin.promoter.code}
                          </div>
                          {checkin.promoter.name && (
                            <div className="text-[#B9B9B9] text-xs">{checkin.promoter.name}</div>
                          )}
                        </span>
                      </div>
                    ) : (
                      <div className="text-[#6F6F6F] text-xs italic">No promoter attached</div>
                    )}
                    {checkin.p_code_entered && (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-[#6F6F6F] text-xs">Entered P-code</span>
                        <span className="text-sm font-mono text-[#B9B9B9]">{checkin.p_code_entered}</span>
                      </div>
                    )}
                    {checkin.promoter_attribution_source && (
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-[#6F6F6F] text-xs">Source</span>
                        <span className="text-[10px] uppercase tracking-wider text-[#B9B9B9]">{checkin.promoter_attribution_source}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Status Update */}
          <div className="surface-card card-highlight rounded-xl p-5">
            <h3 className="font-['EB_Garamond'] text-base text-[#F2F2F2] mb-3">Update Status</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(CHECK_IN_STATUS_LABELS).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  disabled={saving || checkin.check_in_status === key}
                  onClick={() => handleStatusUpdate(key)}
                  className={`text-xs h-8 rounded-lg transition-colors ${
                    checkin.check_in_status === key
                      ? 'bg-[#D4A843]/20 border border-[#D4A843]/40 text-[#D4A843] cursor-default'
                      : 'bg-[#1A1A1A] border border-[#2A2A2A] text-[#B9B9B9] hover:border-[#D4A843]/40'
                  }`}
                  data-testid={`status-btn-${key}`}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Right — Payment + Pricing + Audit */}
        <div className="space-y-4">
          {/* Payment Card */}
          <div className="surface-card card-highlight rounded-xl p-5">
            <h2 className="font-['EB_Garamond'] text-lg text-[#F2F2F2] mb-4">Payment</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[#6F6F6F] text-xs">Total Amount</span>
                <span className="font-['EB_Garamond'] text-xl text-[#D4A843]">{formatCurrency(checkin.total_amount)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#6F6F6F] text-xs">Payment Status</span>
                <span className={`text-xs px-2.5 py-1 rounded-full ${getPaymentStatusClass(checkin.payment_status)}`}>
                  {PAYMENT_STATUS_LABELS[checkin.payment_status]}
                </span>
              </div>
              {payment && (
                <>
                  <div className="border-t border-[#2A2A2A] pt-3 space-y-2">
                    {[
                      { label: 'Order ID', value: payment.order_id, mono: true },
                      { label: 'Transaction ID', value: payment.transaction_id || '—', mono: true },
                      { label: 'Provider', value: payment.provider },
                      { label: 'Created', value: formatDateTime(payment.created_at) },
                    ].map(row => (
                      <div key={row.label} className="flex items-start justify-between gap-3">
                        <span className="text-[#6F6F6F] text-xs">{row.label}</span>
                        <span className={`text-xs text-right max-w-[200px] truncate ${row.mono ? 'text-mono text-[#B9B9B9]' : 'text-[#B9B9B9]'}`}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Pricing Snapshot */}
          {checkin.pricing_snapshot && (
            <div className="surface-card card-highlight rounded-xl p-5">
              <h3 className="font-['EB_Garamond'] text-base text-[#F2F2F2] mb-3">Pricing Snapshot</h3>
              <div className="space-y-2">
                {[
                  { label: 'Rule Type', value: checkin.pricing_snapshot.rule_type || '—' },
                  { label: 'Amount', value: formatCurrency(checkin.pricing_snapshot.amount) },
                  { label: 'Type', value: checkin.pricing_snapshot.amount_type },
                  { label: 'Computed Total', value: formatCurrency(checkin.pricing_snapshot.computed_total) },
                  { label: 'Captured At', value: checkin.pricing_snapshot.captured_at ? formatDateTime(checkin.pricing_snapshot.captured_at) : '—' },
                ].filter(r => r.value && r.value !== '—').map(row => (
                  <div key={row.label} className="flex justify-between items-center text-xs">
                    <span className="text-[#6F6F6F]">{row.label}</span>
                    <span className="text-[#B9B9B9]">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Audit Log */}
          {audit_logs?.length > 0 && (
            <div className="surface-card card-highlight rounded-xl p-5">
              <h3 className="font-['EB_Garamond'] text-base text-[#F2F2F2] mb-3">Activity Log</h3>
              <div className="space-y-2">
                {audit_logs.map(log => (
                  <div key={log._id} className="flex items-start gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4A843] mt-1.5 flex-shrink-0" />
                    <div>
                      <span className="text-[#F2F2F2]">{log.action}</span>
                      <span className="text-[#6F6F6F] ml-2">{formatDateTime(log.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}
